<!-- Long text field with @mentions and light formatting for the dialogs: a box (.mention-box, with the look passed in
     class) holding a mini toolbar and the contenteditable .mention-field. Toolbar: headings (menu: normal text, Heading
     1, 2, 3), bold, italic, underline, strikethrough, bulleted and numbered list, checklist (a tap on an item's box ticks
     it), and @ to tag a video, hook, creator, source or analysed video. The "@" menu (#mentionMenu) and the plain-text
     paste come from src/features/mentionMenu.ts, which listens on the document for every .mention-field. The text is
     stored as before (mentions as "@[type:id]", line breaks as \n) with a few Markdown marks for the formatting: drawn
     with richHtml and read back with domToRich (src/features/richText.ts), so old texts read the same.
     v-model: the text. Changes are read with a MutationObserver (a mention picked from the menu fires no input event; a
     ticked item changes only an attribute). The id and the other attributes go on the editable field; the class on the
     box. -->
<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, useAttrs, watch } from 'vue'
import { usePopover } from '../../composables/usePopover'
import { useI18n } from '../../composables/useI18n'
import { domToRich, richHtml } from '../../features/richText'

defineOptions({ inheritAttrs: false })
const props = withDefaults(defineProps<{ modelValue: string; placeholder?: string }>(), { placeholder: '' })
const emit = defineEmits<{ 'update:modelValue': [text: string] }>()
const attrs = useAttrs()
const boxAttrs = computed(() => ({ class: attrs.class, style: attrs.style }))
const fieldAttrs = computed(() => Object.fromEntries(Object.entries(attrs).filter(([k]) => k !== 'class' && k !== 'style')))
const { t } = useI18n()

const el = ref<HTMLElement | null>(null)
let last = props.modelValue
let observer: MutationObserver | null = null

/** The field's text now (with mentions as "@[type:id]" and the formatting marks) */
function read(): string { return el.value ? domToRich(el.value) : last }
function changed() {
  const v = read()
  if (v !== last) { last = v; emit('update:modelValue', v) }
}
const draw = (v: string) => { if (el.value) el.value.innerHTML = richHtml(v, false) }
// A new value from outside (not typed here) redraws the field
watch(() => props.modelValue, v => { if (v !== last && el.value) { last = v; draw(v) } })

// ===== Toolbar =====
type Block = '' | 'h1' | 'h2' | 'h3'
const state = ref({ bold: false, italic: false, underline: false, strikeThrough: false, block: '' as Block, list: '' as '' | 'ul' | 'ol' | 'task' })
/** The element the caret is in, when it is inside this field */
function caretEl(): HTMLElement | null {
  const s = getSelection(), f = el.value
  if (!s || !s.rangeCount || !f) return null
  const n = s.anchorNode
  const e = n && (n.nodeType === 1 ? n as HTMLElement : n.parentElement)
  return e && f.contains(e) ? e : null
}
function refresh(): void {
  const e = caretEl()
  if (!e) return
  const q = (c: string) => { try { return document.queryCommandState(c) } catch { return false } }
  const h = e.closest('h1, h2, h3'), li = e.closest('li'), list = li?.parentElement
  state.value = {
    bold: q('bold'), italic: q('italic'), underline: q('underline'), strikeThrough: q('strikeThrough'),
    block: h && el.value?.contains(h) ? h.tagName.toLowerCase() as Block : '',
    list: list && el.value?.contains(list) ? (list.tagName === 'OL' ? 'ol' : list.classList.contains('mf-tasks') ? 'task' : 'ul') : ''
  }
}
/** Puts the caret in the field (at the end) when it is somewhere else, so a button works before typing */
function focusField(): void {
  const f = el.value
  if (!f || caretEl()) return
  f.focus()
  const r = document.createRange(); r.selectNodeContents(f); r.collapse(false)
  const s = getSelection(); s?.removeAllRanges(); s?.addRange(r)
}
function exec(cmd: string, value?: string): void {
  focusField()
  try { document.execCommand('styleWithCSS', false, 'false') } catch { /* not supported */ }
  document.execCommand(cmd, false, value)
  refresh()
}
const fmt = (cmd: 'bold' | 'italic' | 'underline' | 'strikeThrough') => exec(cmd)
// Headings menu
const headOpen = ref(false)
const headRoot = ref<HTMLElement | null>(null)
usePopover(headOpen, headRoot, { esc: true })
function heading(b: Block): void {
  headOpen.value = false
  exec('formatBlock', b && state.value.block !== b ? b : 'div')
}
/** The list around the caret (in this field) */
const caretList = (): HTMLElement | null => { const l = caretEl()?.closest('li')?.parentElement; return l && el.value?.contains(l) ? l : null }
function list(kind: 'ul' | 'ol' | 'task'): void {
  focusField()
  const cur = caretList(), tasks = !!cur?.classList.contains('mf-tasks')
  if (kind === 'ol') { exec('insertOrderedList'); return }
  if (kind === 'ul') {
    if (tasks && cur) { cur.classList.remove('mf-tasks'); refresh(); return }
    exec('insertUnorderedList'); return
  }
  // checklist: a bulleted list with boxes
  if (tasks) { exec('insertUnorderedList'); return }
  if (!cur || cur.tagName !== 'UL') exec('insertUnorderedList')
  caretList()?.classList.add('mf-tasks')
  refresh()
}
/** @: types "@" at the caret (after a space), which opens the mention menu */
function tag(): void {
  focusField()
  const s = getSelection(), r = s && s.rangeCount ? s.getRangeAt(0) : null
  let before = ''
  if (r && r.startContainer.nodeType === 3) before = (r.startContainer.textContent || '').slice(0, r.startOffset).slice(-1)
  else if (r && r.startOffset > 0) before = r.startContainer.childNodes[r.startOffset - 1]?.textContent?.slice(-1) || ''
  exec('insertText', before && !/\s/.test(before) ? ' @' : '@')
}

// A tap on an item's box ticks it
function onClick(e: MouseEvent): void {
  const li = (e.target as HTMLElement).closest?.('li')
  if (!li || !el.value?.contains(li) || !li.parentElement?.classList.contains('mf-tasks')) return
  const r = li.getBoundingClientRect()
  if (e.clientX - r.left > parseFloat(getComputedStyle(li).fontSize) * 1.5) return
  e.preventDefault()
  li.toggleAttribute('data-done')
}
// A new item made with Enter starts unticked
function onInput(e: Event): void {
  if ((e as InputEvent).inputType !== 'insertParagraph') return
  const li = caretEl()?.closest('li')
  if (li && li.parentElement?.classList.contains('mf-tasks') && !li.textContent?.trim()) li.removeAttribute('data-done')
}
function onSelection(): void { if (caretEl()) refresh() }

onMounted(() => {
  if (!el.value) return
  draw(props.modelValue)
  observer = new MutationObserver(changed)
  observer.observe(el.value, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['class', 'data-done'] })
  document.addEventListener('selectionchange', onSelection)
})
onBeforeUnmount(() => { observer?.disconnect(); document.removeEventListener('selectionchange', onSelection) })
defineExpose({ read, el })

const BTN = 'w-7 h-7 sm:w-8 sm:h-8 flex-shrink-0 rounded-md text-xs flex items-center justify-center transition-colors'
const btn = (on: boolean) => [BTN, on ? 'bg-brand/15 text-brand' : 'text-gray-400 hover:text-gray-100 hover:bg-darkbtn']
const HEADS = computed<[Block, string, string][]>(() => [['', t('Normal text'), 'text-sm'], ['h1', t('Heading 1'), 'text-lg font-black'], ['h2', t('Heading 2'), 'text-base font-extrabold'], ['h3', t('Heading 3'), 'text-sm font-bold']])
</script>

<template>
  <div v-bind="boxAttrs" class="mention-box flex flex-col focus-within:border-brand">
    <!-- Mini toolbar (mousedown keeps the caret in the field) -->
    <div class="relative -mt-1 mb-2 pb-1.5 border-b border-gray-800" @mousedown.prevent>
      <div class="flex flex-wrap items-center gap-0.5" role="toolbar" :aria-label="t('Formatting')">
        <div ref="headRoot" class="relative flex-shrink-0">
          <button type="button" :title="t('Headings')" :aria-label="t('Headings')" :aria-expanded="headOpen" :class="[btn(!!state.block), 'w-auto px-1.5 gap-0.5 font-black']" @click="headOpen = !headOpen">
            <span>{{ state.block ? state.block.toUpperCase() : 'H' }}</span><i class="fas fa-caret-down text-[9px] opacity-70"></i></button>
        </div>
        <span class="hidden sm:block w-px h-5 bg-gray-800 mx-1 flex-shrink-0" aria-hidden="true"></span>
        <button type="button" :title="t('Bold')" :aria-label="t('Bold')" :aria-pressed="state.bold" :class="btn(state.bold)" @click="fmt('bold')"><i class="fas fa-bold"></i></button>
        <button type="button" :title="t('Italic')" :aria-label="t('Italic')" :aria-pressed="state.italic" :class="btn(state.italic)" @click="fmt('italic')"><i class="fas fa-italic"></i></button>
        <button type="button" :title="t('Underline')" :aria-label="t('Underline')" :aria-pressed="state.underline" :class="btn(state.underline)" @click="fmt('underline')"><i class="fas fa-underline"></i></button>
        <button type="button" :title="t('Strikethrough')" :aria-label="t('Strikethrough')" :aria-pressed="state.strikeThrough" :class="btn(state.strikeThrough)" @click="fmt('strikeThrough')"><i class="fas fa-strikethrough"></i></button>
        <span class="hidden sm:block w-px h-5 bg-gray-800 mx-1 flex-shrink-0" aria-hidden="true"></span>
        <button type="button" :title="t('Bulleted list')" :aria-label="t('Bulleted list')" :aria-pressed="state.list === 'ul'" :class="btn(state.list === 'ul')" @click="list('ul')"><i class="fas fa-list-ul"></i></button>
        <button type="button" :title="t('Numbered list')" :aria-label="t('Numbered list')" :aria-pressed="state.list === 'ol'" :class="btn(state.list === 'ol')" @click="list('ol')"><i class="fas fa-list-ol"></i></button>
        <button type="button" :title="t('To-do list')" :aria-label="t('To-do list')" :aria-pressed="state.list === 'task'" :class="btn(state.list === 'task')" @click="list('task')"><i class="fas fa-square-check"></i></button>
        <span class="hidden sm:block w-px h-5 bg-gray-800 mx-1 flex-shrink-0" aria-hidden="true"></span>
        <button type="button" :title="t('Tag a video, hook, creator or source')" :aria-label="t('Tag a video, hook, creator or source')" :class="[btn(false), 'font-black text-sm']" @click="tag">@</button>
      </div>
      <!-- Headings menu, under the toolbar -->
      <div v-if="headOpen" class="absolute z-30 top-full left-0 mt-1 w-44 p-1.5 bg-darkcard border border-gray-800 rounded-xl shadow-2xl" role="menu">
        <button v-for="[b, label, cls] in HEADS" :key="b" type="button" role="menuitem" :class="['w-full text-left px-3 py-1.5 rounded-lg', cls, state.block === b ? 'bg-brand/15 text-brand' : 'text-gray-200 hover:bg-brand/10']" @click="heading(b)">{{ label }}</button>
      </div>
    </div>
    <div v-bind="fieldAttrs" ref="el" contenteditable="true" :data-ph="placeholder" class="mention-field flex-1 min-h-[3rem] focus:outline-none" @click="onClick" @input="onInput"></div>
  </div>
</template>
